import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Users, Plus, Edit, Trash2, Calendar, CreditCard, AlertCircle, TrendingUp, CreditCard as CardIcon, UserPlus, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { useTranslation } from "../lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const { token } = useAuth();
  const { t, language } = useTranslation();
  const [students, setStudents] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [cardLookupLoading, setCardLookupLoading] = useState(false);
  const [balanceCalculation, setBalanceCalculation] = useState(null);
  const [newAdmin, setNewAdmin] = useState({
    username: "",
    password: "",
    role: "admin"
  });
  const [newStudent, setNewStudent] = useState({
    name: "",
    surname: "",
    school_id: "",
    card_id: "",
    meal_type: "table_dhote",
    duration_days: 30,
    meals_per_day: 1,
    custom_balance: null
  });

  const axiosConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  };

  useEffect(() => {
    fetchStudents();
    fetchStatistics();
    fetchAdmins();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/students`, axiosConfig);
      setStudents(response.data);
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error(t("adminPanel.notifications.fetchStudentsError"));
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${API}/statistics`, axiosConfig);
      setStatistics(response.data);
    } catch (error) {
      console.error("Error fetching statistics:", error);
    }
  };

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admins`, axiosConfig);
      setAdmins(response.data);
    } catch (error) {
      console.error("Error fetching admins:", error);
    }
  };

  const calculateBalance = async (mealType, durationDays, mealsPerDay) => {
    if (mealType === 'custom') {
      setBalanceCalculation({
        base_price: 0,
        duration_multiplier: 1,
        meals_multiplier: 1,
        total_balance: 0,
        meal_type: 'custom',
        duration_days: durationDays,
        meals_per_day: mealsPerDay
      });
      return;
    }

    try {
      const response = await axios.post(`${API}/calculate-balance`, {
        meal_type: mealType,
        duration_days: durationDays,
        meals_per_day: mealsPerDay
      }, axiosConfig);
      setBalanceCalculation(response.data);
    } catch (error) {
      console.error("Error calculating balance:", error);
    }
  };

  const handleCardScan = async (cardId) => {
    if (!cardId.trim()) return;
    
    setCardLookupLoading(true);
    try {
      const response = await axios.get(`${API}/card-lookup/${cardId.trim()}`, axiosConfig);
      
      let updatedStudent;

      if (response.data.exists) {
        // Card exists, fill form with existing data
        const student = response.data.student;
        const today = new Date();
        const expireDate = new Date(student.expire_date);
        const isExpired = today > expireDate;
        const isActive = student.is_active && !isExpired;
        const studentName = `${student.name} ${student.surname}`;

        updatedStudent = {
          name: student.name,
          surname: student.surname,
          school_id: student.school_id,
          card_id: student.card_id,
          meal_type: "table_dhote", // Reset to default for new calculation
          duration_days: 30,
          meals_per_day: 1,
          custom_balance: null
        };

        setNewStudent(updatedStudent);

        if (isActive) {
          toast.warning(
            t("adminPanel.notifications.cardActive", {
              name: studentName,
              balance: formatPrice(student.balance),
              expires: formatDate(student.expire_date)
            })
          );
        } else if (isExpired) {
          toast.info(
            t("adminPanel.notifications.cardExpired", {
              name: studentName,
              balance: formatPrice(student.balance),
              expires: formatDate(student.expire_date)
            })
          );
        } else {
          toast.info(
            t("adminPanel.notifications.cardInactive", {
              name: studentName,
              balance: formatPrice(student.balance)
            })
          );
        }
      } else {
        // New card, just set the card_id
        updatedStudent = {
          ...newStudent,
          card_id: cardId.trim(),
          name: "",
          surname: "",
          school_id: ""
        };
        setNewStudent(updatedStudent);
        toast.success(t("adminPanel.notifications.newCard"));
      }

      await calculateBalance(
        updatedStudent.meal_type,
        updatedStudent.duration_days,
        updatedStudent.meals_per_day
      );
    } catch (error) {
      console.error("Error looking up card:", error);
      toast.error(t("adminPanel.notifications.lookupError"));
    } finally {
      setCardLookupLoading(false);
    }
  };

  // Auto-trigger card check on Enter key
  const handleCardIdKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCardScan(newStudent.card_id);
    }
  };

  // Update balance calculation when form values change
  useEffect(() => {
    if (newStudent.meal_type && newStudent.duration_days && newStudent.meals_per_day) {
      calculateBalance(newStudent.meal_type, newStudent.duration_days, newStudent.meals_per_day);
    }
  }, [newStudent.meal_type, newStudent.duration_days, newStudent.meals_per_day]);

  const handleAddStudent = async (e) => {
    e.preventDefault();
    if (!newStudent.name || !newStudent.surname || !newStudent.school_id || !newStudent.card_id) {
      toast.error(t("adminPanel.notifications.addError"));
      return;
    }

    try {
      const studentData = { ...newStudent };
      if (newStudent.meal_type === 'custom' && newStudent.custom_balance !== null) {
        studentData.custom_balance = parseFloat(newStudent.custom_balance);
      }
      
      const response = await axios.post(`${API}/students`, studentData, axiosConfig);
      setStudents([response.data, ...students]);
      setNewStudent({
        name: "",
        surname: "",
        school_id: "",
        card_id: "",
        meal_type: "table_dhote",
        duration_days: 30,
        meals_per_day: 1,
        custom_balance: null
      });
      setBalanceCalculation(null);
      setAddDialogOpen(false);
      fetchStatistics();
      toast.success(
        t("adminPanel.notifications.addSuccess", {
          name: response.data.name,
          surname: response.data.surname,
          balance: formatPrice(response.data.balance)
        })
      );
    } catch (error) {
      console.error("Error adding student:", error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(t("adminPanel.notifications.addFailed"));
      }
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!newAdmin.username || !newAdmin.password) {
      toast.error(t("adminPanel.notifications.adminAddError"));
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/create`, newAdmin, axiosConfig);
      setAdmins([...admins, response.data]);
      setNewAdmin({ username: "", password: "", role: "admin" });
      setAdminDialogOpen(false);
      toast.success(
        t("adminPanel.notifications.adminAddSuccess", { username: response.data.username })
      );
    } catch (error) {
      console.error("Error adding admin:", error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(t("adminPanel.notifications.adminAddFailed"));
      }
    }
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    if (!selectedStudent) return;

    try {
      const updateData = {
        name: selectedStudent.name,
        surname: selectedStudent.surname,
        school_id: selectedStudent.school_id,
        balance: selectedStudent.balance
      };
      
      const response = await axios.put(`${API}/students/${selectedStudent.id}`, updateData, axiosConfig);
      setStudents(students.map(s => s.id === selectedStudent.id ? response.data : s));
      setEditDialogOpen(false);
      setSelectedStudent(null);
      fetchStatistics();
      toast.success(
        t("adminPanel.notifications.updateSuccess", {
          balance: formatPrice(response.data.balance)
        })
      );
    } catch (error) {
      console.error("Error updating student:", error);
      toast.error(t("adminPanel.notifications.updateError"));
    }
  };

  const handleDeleteStudent = async (studentId, studentName) => {
    if (!window.confirm(
      t("adminPanel.deleteConfirm.description", { name: studentName })
    )) return;

    try {
      await axios.delete(`${API}/students/${studentId}`, axiosConfig);
      setStudents(students.filter(s => s.id !== studentId));
      fetchStatistics();
      toast.success(
        t("adminPanel.notifications.deleteSuccess", { name: studentName })
      );
    } catch (error) {
      console.error("Error deleting student:", error);
      toast.error(t("adminPanel.notifications.deleteError"));
    }
  };

  const handleCleanupExpired = async () => {
    try {
      await axios.post(`${API}/cleanup-expired`, {}, axiosConfig);
      toast.success(t("adminPanel.notifications.cleanupSuccess"));
      fetchStudents();
      fetchStatistics();
    } catch (error) {
      console.error("Error cleaning up expired subscriptions:", error);
      toast.error(t("adminPanel.notifications.cleanupError"));
    }
  };

  const getStatusBadge = (student) => {
    const today = new Date();
    const expireDate = new Date(student.expire_date);
    const daysUntilExpiry = Math.ceil((expireDate - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return <Badge className="bg-red-100 text-red-800 border-red-200">{t("adminPanel.status.expired")}</Badge>;
    } else if (daysUntilExpiry <= 7) {
      return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">{t("adminPanel.status.expiringSoon")}</Badge>;
    } else if (student.balance <= 0) {
      return <Badge className="bg-orange-100 text-orange-800 border-orange-200">{t("adminPanel.status.noBalance")}</Badge>;
    } else {
      return <Badge className="bg-green-100 text-green-800 border-green-200">{t("adminPanel.status.active")}</Badge>;
    }
  };

  const getMealTypeBadge = (mealType) => {
    switch (mealType) {
      case 'table_dhote':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">{t("adminPanel.mealTypes.table_dhote")}</Badge>;
      case 'alacarte':
        return <Badge className="bg-purple-100 text-purple-800 border-purple-200">{t("adminPanel.mealTypes.alacarte")}</Badge>;
      case 'custom':
        return <Badge className="bg-gray-100 text-gray-800 border-gray-200">{t("adminPanel.mealTypes.custom")}</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800 border-gray-200">{t("adminPanel.mealTypes.unknown")}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t("adminPanel.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t("adminPanel.title")}</h1>
          <p className="text-gray-600">{t("adminPanel.description")}</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-blue-600" />
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-600">{t("adminPanel.stats.totalStudents")}</p>
                  <p className="text-xl font-bold text-gray-900">{statistics.total_students || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <Calendar className="h-8 w-8 text-green-600" />
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-600">{t("adminPanel.stats.activeStudents")}</p>
                  <p className="text-xl font-bold text-gray-900">{statistics.active_students || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <AlertCircle className="h-8 w-8 text-yellow-600" />
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-600">{t("adminPanel.stats.expiringSoon")}</p>
                  <p className="text-xl font-bold text-gray-900">{statistics.expiring_soon || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <CreditCard className="h-8 w-8 text-red-600" />
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-600">{t("adminPanel.stats.expired")}</p>
                  <p className="text-xl font-bold text-gray-900">{statistics.expired_students || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <CardIcon className="h-8 w-8 text-purple-600" />
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-600">{t("adminPanel.stats.transactionsToday")}</p>
                  <p className="text-xl font-bold text-gray-900">{statistics.transactions_today || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <Wallet className="h-8 w-8 text-indigo-600" />
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-600">{t("adminPanel.stats.totalBalance")}</p>
                  <p className="text-lg font-bold text-gray-900">{formatPrice(statistics.total_balance || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <TrendingUp className="h-8 w-8 text-green-600" />
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-600">{t("adminPanel.stats.revenue")}</p>
                  <p className="text-lg font-bold text-gray-900">{formatPrice(statistics.total_revenue || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 mb-6">
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" />
                {t("adminPanel.addStudent")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{t("adminPanel.addStudent")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddStudent} className="space-y-4">
                {/* Card ID Scanner */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <Label htmlFor="card_id">{t("adminPanel.form.scanPrompt")}</Label>
                  <div className="flex space-x-2">
                    <Input
                      id="card_id"
                      type="text"
                      value={newStudent.card_id}
                      onChange={(e) => setNewStudent({...newStudent, card_id: e.target.value})}
                      onKeyPress={handleCardIdKeyPress}
                      placeholder={t("adminPanel.form.lookupPlaceholder")}
                      className="font-mono"
                      required
                    />
                    <Button 
                      type="button" 
                      onClick={() => handleCardScan(newStudent.card_id)}
                      disabled={!newStudent.card_id || cardLookupLoading}
                      variant="outline"
                    >
                      {cardLookupLoading ? t("adminPanel.form.scanning") : t("adminPanel.form.lookup")}
                  </Button>
                </div>
                <p className="text-sm text-blue-600 mt-1">
                    {t("adminPanel.form.scanHelp")}
                </p>
              </div>

                {/* Student Information */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="name">{t("adminPanel.form.name")} *</Label>
                    <Input
                      id="name"
                      type="text"
                      value={newStudent.name}
                      onChange={(e) => setNewStudent({...newStudent, name: e.target.value})}
                      placeholder={t("adminPanel.placeholders.name")}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="surname">{t("adminPanel.form.surname")} *</Label>
                    <Input
                      id="surname"
                      type="text"
                      value={newStudent.surname}
                      onChange={(e) => setNewStudent({...newStudent, surname: e.target.value})}
                      placeholder={t("adminPanel.placeholders.surname")}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="school_id">{t("adminPanel.form.schoolId")} *</Label>
                    <Input
                      id="school_id"
                      type="text"
                      value={newStudent.school_id}
                      onChange={(e) => setNewStudent({...newStudent, school_id: e.target.value})}
                      placeholder={t("adminPanel.placeholders.schoolId")}
                      required
                    />
                  </div>
                </div>

                {/* Balance Configuration */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="meal_type">{t("adminPanel.form.mealType")}</Label>
                    <Select
                      value={newStudent.meal_type}
                      onValueChange={(value) => setNewStudent({...newStudent, meal_type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="table_dhote">{t("adminPanel.mealOptions.tableDhote")}</SelectItem>
                        <SelectItem value="alacarte">{t("adminPanel.mealOptions.alacarte")}</SelectItem>
                        <SelectItem value="custom">{t("adminPanel.mealOptions.custom")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="duration_days">{t("adminPanel.form.duration")}</Label>
                    <Select
                      value={newStudent.duration_days.toString()}
                      onValueChange={(value) => setNewStudent({...newStudent, duration_days: parseInt(value)})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">{t("adminPanel.durationOptions.30")}</SelectItem>
                        <SelectItem value="60">{t("adminPanel.durationOptions.60")}</SelectItem>
                        <SelectItem value="90">{t("adminPanel.durationOptions.90")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="meals_per_day">{t("adminPanel.form.mealsPerDay")}</Label>
                    <Select
                      value={newStudent.meals_per_day.toString()}
                      onValueChange={(value) => setNewStudent({...newStudent, meals_per_day: parseInt(value)})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">{t("adminPanel.mealsPerDayOptions.one")}</SelectItem>
                        <SelectItem value="2">{t("adminPanel.mealsPerDayOptions.two")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Custom Balance Input */}
                {newStudent.meal_type === 'custom' && (
                  <div>
                    <Label htmlFor="custom_balance">{t("adminPanel.form.customBalance")}</Label>
                    <Input
                      id="custom_balance"
                      type="number"
                      step="0.01"
                      value={newStudent.custom_balance || ''}
                      onChange={(e) => setNewStudent({...newStudent, custom_balance: e.target.value ? parseFloat(e.target.value) : null})}
                      placeholder={t("adminPanel.placeholders.customBalance")}
                    />
                  </div>
                )}

                {/* Balance Calculation Display */}
                {balanceCalculation && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-green-800 mb-2">{t("adminPanel.form.calculate")}</h3>
                    {newStudent.meal_type === 'custom' ? (
                      <div className="text-sm">
                        <p className="text-lg font-bold text-green-800">
                          <strong>{t("adminPanel.balance.custom", { amount: formatPrice(newStudent.custom_balance || 0) })}</strong>
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p><strong>{t("adminPanel.balance.base")}</strong> {formatPrice(balanceCalculation.base_price)}</p>
                          <p><strong>{t("adminPanel.balance.duration")}</strong> {balanceCalculation.duration_days} {t("adminPanel.balance.days")} (×{balanceCalculation.duration_multiplier})</p>
                        </div>
                        <div>
                          <p><strong>{t("adminPanel.balance.mealsPerDay")}</strong> {balanceCalculation.meals_per_day} (×{balanceCalculation.meals_multiplier})</p>
                          <p className="text-lg font-bold text-green-800">
                            <strong>{t("adminPanel.balance.total", { amount: formatPrice(balanceCalculation.total_balance) })}</strong>
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>
                    {t("adminPanel.form.cancel")}
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    {t("adminPanel.form.submit")}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={adminDialogOpen} onOpenChange={setAdminDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50">
                <UserPlus className="h-4 w-4 mr-2" />
                {t("adminPanel.addAdmin")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("adminPanel.adminForm.title")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddAdmin} className="space-y-4">
                <div>
                  <Label htmlFor="admin_username">{t("adminPanel.adminForm.username")} *</Label>
                  <Input
                    id="admin_username"
                    type="text"
                    value={newAdmin.username}
                    onChange={(e) => setNewAdmin({...newAdmin, username: e.target.value})}
                    placeholder={t("adminPanel.adminForm.usernamePlaceholder")}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="admin_password">{t("adminPanel.adminForm.password")} *</Label>
                  <Input
                    id="admin_password"
                    type="password"
                    value={newAdmin.password}
                    onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                    placeholder={t("adminPanel.adminForm.passwordPlaceholder")}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="admin_role">{t("adminPanel.adminForm.role")}</Label>
                  <Select
                    value={newAdmin.role}
                    onValueChange={(value) => setNewAdmin({...newAdmin, role: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">{t("adminPanel.roles.admin")}</SelectItem>
                      <SelectItem value="cashier">{t("adminPanel.roles.cashier")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-blue-800">{t("adminPanel.roles.help")}</p>
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setAdminDialogOpen(false)}>
                    {t("adminPanel.form.cancel")}
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    {t("adminPanel.adminForm.submit")}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Button
            onClick={handleCleanupExpired}
            variant="outline"
            className="border-yellow-300 text-yellow-700 hover:bg-yellow-50"
          >
            <AlertCircle className="h-4 w-4 mr-2" />
            {t("adminPanel.cleanup")}
          </Button>
        </div>

        {/* Students Table */}
        <Card>
          <CardHeader>
            <CardTitle>{t("adminPanel.studentsCard.title")}</CardTitle>
          </CardHeader>
          <CardContent>
            {students.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">{t("adminPanel.table.noStudents")}</p>
                <p className="text-gray-400">{t("adminPanel.table.emptyHelper")}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("adminPanel.table.name")}</TableHead>
                      <TableHead>{t("adminPanel.table.cardId")}</TableHead>
                      <TableHead>{t("adminPanel.table.schoolId")}</TableHead>
                      <TableHead>{t("adminPanel.table.balance")}</TableHead>
                      <TableHead>{t("adminPanel.table.expires")}</TableHead>
                      <TableHead>{t("adminPanel.table.status")}</TableHead>
                      <TableHead>{t("adminPanel.table.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {students.map((student) => (
                      <TableRow key={student.id}>
                        <TableCell className="font-medium">
                          {student.name} {student.surname}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{student.card_id}</TableCell>
                        <TableCell>{student.school_id}</TableCell>
                        <TableCell className={`font-semibold ${student.balance <= 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatPrice(student.balance)}
                        </TableCell>
                        <TableCell>{formatDate(student.expire_date)}</TableCell>
                        <TableCell>{getStatusBadge(student)}</TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedStudent(student);
                                setEditDialogOpen(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteStudent(student.id, `${student.name} ${student.surname}`)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t("adminPanel.editStudent")}</DialogTitle>
            </DialogHeader>
            {selectedStudent && (
              <form onSubmit={handleUpdateStudent} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="edit_name">{t("adminPanel.form.name")}</Label>
                    <Input
                      id="edit_name"
                      type="text"
                      value={selectedStudent.name}
                      onChange={(e) => setSelectedStudent({...selectedStudent, name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_surname">{t("adminPanel.form.surname")}</Label>
                    <Input
                      id="edit_surname"
                      type="text"
                      value={selectedStudent.surname}
                      onChange={(e) => setSelectedStudent({...selectedStudent, surname: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_school_id">{t("adminPanel.form.schoolId")}</Label>
                    <Input
                      id="edit_school_id"
                      type="text"
                      value={selectedStudent.school_id}
                      onChange={(e) => setSelectedStudent({...selectedStudent, school_id: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit_card_id">{t("adminPanel.form.cardIdReadOnly")}</Label>
                  <Input
                    id="edit_card_id"
                    type="text"
                    value={selectedStudent.card_id}
                    readOnly
                    className="bg-gray-100 font-mono"
                  />
                </div>

                <div>
                  <Label htmlFor="edit_balance">{t("adminPanel.form.balance")}</Label>
                  <Input
                    id="edit_balance"
                    type="number"
                    step="0.01"
                    value={selectedStudent.balance}
                    onChange={(e) => setSelectedStudent({...selectedStudent, balance: parseFloat(e.target.value)})}
                    required
                  />
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    {t("adminPanel.editHelp")}
                  </p>
                </div>

                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                    {t("adminPanel.form.cancel")}
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    {t("adminPanel.form.update")}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPanel;